import React, { ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import BottomNav from './BottomNav';
import { useAuth, usePullToRefresh } from '../../hooks';
import { PullToRefreshIndicator } from '../UI';
// Development auth removed for production build

interface LayoutProps {
  children: ReactNode;
  hideHeader?: boolean;
  hideBottomNav?: boolean;
  headerTitle?: string;
  showBackButton?: boolean;
  backButtonAction?: () => void;
  rightElement?: ReactNode;
  onRefresh?: () => Promise<void>;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  hideHeader = false,
  hideBottomNav = false,
  headerTitle,
  showBackButton = false,
  backButtonAction,
  rightElement,
  onRefresh
}) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const mainRef = useRef<HTMLDivElement>(null);

  // Reset scroll position on route change
  useEffect(() => {
    // Force window scroll to 0 (for browser history scroll restoration)
    window.scrollTo(0, 0);

    // Also reset main container scroll
    if (mainRef.current) {
      mainRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]);

  // Pull to refresh hook — only enable when the page actually provides a
  // refresh handler. Otherwise the gesture showed a spinner that slept 1s and
  // refreshed nothing, on every page.
  const { containerRef, isRefreshing, pullDistance, pullProgress } = usePullToRefresh({
    enabled: !!onRefresh,
    onRefresh: onRefresh ?? (async () => {}),
    threshold: 80
  });
  
  // Disable swipe navigation on this layout - it's interfering with bottom nav
  // Individual pages can enable it if needed
  // useSwipeNavigation({
  //   enabled: true,
  //   onSwipeBack: backButtonAction // Use custom back action if provided
  // });

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Fixed Header */}
      {!hideHeader && (
        <div className="bg-white dark:bg-gray-800 z-50 layout-header-safe-area">
          <Header
            title={headerTitle}
            showBackButton={showBackButton}
            backButtonAction={backButtonAction}
            rightElement={rightElement}
          />
        </div>
      )}
      
      {/* Scrollable Main Content */}
      <main
        ref={(node) => {
          // @ts-ignore
          if (containerRef) containerRef.current = node;
          // @ts-ignore
          if (mainRef) mainRef.current = node;
        }}
        className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 relative"
      >
        {/* Pull to refresh indicator */}
        {pullDistance > 0 && (
          <PullToRefreshIndicator 
            pullDistance={pullDistance}
            isRefreshing={isRefreshing}
            pullProgress={pullProgress}
          />
        )}
        {children}
      </main>
      
      {/* Fixed Bottom Navigation */}
      {!hideBottomNav && isAuthenticated && <BottomNav />}
    </div>
  );
};

export default Layout;